import React, { useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Beaker } from 'lucide-react';
import { toast } from 'sonner';

const Auth = () => {
  const { login } = React.useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'formulator',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.token, response.data.user);
      toast.success('Connexion reussie !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      login(response.data.token, response.data.user);
      toast.success('Compte cree avec succes !');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1746479019530-c7dd09f1673b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGNvc21ldGljJTIwbGFib3JhdG9yeXxlbnwwfHx8fDE3NzUzMTU3MDF8MA&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm"></div>
      
      <Card className="w-full max-w-md relative z-10 border-[#E5E5DF] shadow-lg">
        <CardContent className="p-8">
          <div className="flex flex-col items-center mb-6">
            <Beaker className="h-12 w-12 text-[#2F4840] mb-3" />
            <h1 className="text-3xl font-semibold heading-font text-[#2F4840] text-center" data-testid="auth-title">
              R&D Cosmetique
            </h1>
            <p className="text-lg font-medium text-[#1A1C1B] mt-1">by DumaniLab</p>
            <p className="text-sm text-[#5C615E] mt-2 text-center">
              Plateforme de simulation de formulation cosmetique
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="mt-1 border-[#E5E5DF] focus:ring-[#9BBAB0]"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-1 border-[#E5E5DF] focus:ring-[#9BBAB0]"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#2F4840] hover:bg-[#22352F] text-white"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="register-name">Nom complet</Label>
                  <Input
                    id="register-name"
                    type="text"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                    className="mt-1 border-[#E5E5DF] focus:ring-[#9BBAB0]"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="mt-1 border-[#E5E5DF] focus:ring-[#9BBAB0]"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Mot de passe</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="mt-1 border-[#E5E5DF] focus:ring-[#9BBAB0]"
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-role">Role</Label>
                  <select
                    id="register-role"
                    value={registerData.role}
                    onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0] text-sm"
                    data-testid="register-role-select"
                  >
                    <option value="formulator">Formulateur</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#2F4840] hover:bg-[#22352F] text-white"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? 'Creation...' : "Creer un compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
